import cx from 'classnames'
import Img from 'gatsby-image'
// @ts-ignore
import { getFluidGatsbyImage, getFluidProps } from 'gatsby-source-sanity'
import React, { useState, useEffect } from 'react'

import imageUrlBuilder from '@sanity/image-url'
import sanityClient from '@sanity/client'
import { SanityImageSource } from '@sanity/image-url/lib/types/types'

const sanityConfig = {
  projectId: process.env.GATSBY_SANITY_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_DATASET,
}

const client = sanityClient({
  projectId: process.env.GATSBY_SANITY_PROJECT_ID,
  dataset: process.env.GATSBY_SANITY_DATASET,
  useCdn: false, // `false` if you want to ensure fresh data
})

export const loadLazyImage = function () {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.intersectionRatio > 0) {
          if (entry.target.hasAttribute('data-src')) {
            entry.target.setAttribute(
              'src',
              entry.target.getAttribute('data-src') as string
            )
            observer.unobserve(entry.target)
          }
        }
      })
    })
    document.querySelectorAll('.lazy-image').forEach(imageElement => {
      if (
        imageElement.getAttribute('is-observed') != 'true' &&
        imageElement.getAttribute('data-src') != null
      ) {
        imageElement.setAttribute('is-observed', 'true')
        observer.observe(imageElement)
      }
    })
  } else {
    const imgList = document.querySelectorAll('.lazy-image')
    Array.prototype.forEach.call(imgList, function (image) {
      image.setAttribute('src', image.getAttribute('data-src'))
    })
  }
}

export const Image = ({
  imageId,
  className,
  size,
  width,
  alt,
  src,
  onClick,
  raw,
  lazy,
  fromArticle,
  cls,
  rawStyleOverride
}: {
  imageId?: string
  width?: number
  alt?: string
  size?: number
  onClick?: any
  raw?: object
  className?: string
  src?: string
  lazy?: boolean
  fromArticle?: boolean
  cls?: string
  rawStyleOverride?: any
}) => {
  const [loaded, setLoaded] = useState(false)
  const [imageWidth, setImageWidth] = useState(768)
  const [sourceUrl, setSourceUrl] = useState('')
  const [dataSource, setDataSource] = useState('')

  useEffect(() => {
    loadLazyImage()

    if (!window.matchMedia) return
    const width = window.matchMedia("(pointer:coarse)").matches ? 768 : 1920
    setImageWidth(width)
  }, [])

  useEffect(() => {
    const builder = imageUrlBuilder(client)
    let imageSrc: SanityImageSource = (raw ? raw : imageId) as SanityImageSource
  
    const dataSrc = 'number' === typeof size && size > 0
      ? builder.image(imageSrc)?.width(size)
      : builder.image(imageSrc)?.width(imageWidth)
  
    setDataSource(dataSrc ? dataSrc.url() ?? '' : '')
    setSourceUrl(dataSrc ? dataSrc.format('webp').url() ?? '' : '')
  }, [imageWidth])

  let fluidProps
  let maxImageWidth = 'number' === typeof width ? Math.min(imageWidth, width) : imageWidth
  if (imageId && !/gif/.test(imageId)) {
    fluidProps = getFluidGatsbyImage(
      imageId,
      { maxWidth: maxImageWidth },
      sanityConfig
    )
  }

  return (
    <div onClick={onClick} className={cx('image__block', className)}>
      {fluidProps && (!raw || fromArticle) ? (
          <Img
            loading={!lazy ? 'lazy' : 'auto'}
            className={cx('hidden__image', cls, {
              'lazy-image': !lazy,
            })}
            fluid={fluidProps}
            alt={alt}
          />
      ) : (
        <picture>
          <source srcSet={sourceUrl} type="image/webp"></source>
          <img
            alt={alt}
            style={rawStyleOverride || {}}
            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
            data-src={dataSource}
            className={cx('x y object-fit block', {
              'lazy-image': lazy,
              'is-loaded': loaded
            })}
            onLoad={() => {
              setLoaded(true)
            }}
          />
        </picture>
      )}
    </div>
  )
}

export type SafeImageProps = {
  imageId: string | undefined
  alt: string
  raw?: object | undefined
}

export const SafeImage = (props: SafeImageProps) => {
  const { imageId, alt = '', raw} = props

  if (!raw) {
    return <></>
  }

  return (<Image imageId={imageId} alt={alt} raw={raw} lazy />)
}
